import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip,
  TextField, Switch, FormControlLabel, Snackbar, Alert,
  CircularProgress, Tooltip, Checkbox, MenuItem, Tabs, Tab, Divider,
  TableSortLabel, Avatar
} from '@mui/material';
import { Add, Edit, Delete, Person, Lock, Smartphone, People, SelectAll } from '@mui/icons-material';
import axios from 'axios';
import { extractArrayFromResponse } from '../utils/apiHelpers';
import { Modal } from './common/Modal';

interface Guide {
  id: number;
  name: string;
  short_name: string;
  contract_number: string;
  contract_date: string;
  contract_end_date?: string;
  max_id?: string;
  is_active: boolean | number;
  languages?: string | { language: string; level: string }[];
  programs?: string | string[];
}

type Order = 'asc' | 'desc';

const formatDateDisplay = (dateStr: string): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch { return '-'; }
};

const formatDateForInput = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch { return ''; }
};

const GuidesPage: React.FC = () => {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGuide, setEditingGuide] = useState<Guide | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Сортировка
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Guide>('name');

  const [formData, setFormData] = useState<Partial<Guide>>({
    name: '', short_name: '', contract_number: '', contract_date: '', contract_end_date: '', max_id: '', is_active: true
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Данные для программ и языков
  const [tours, setTours] = useState<any[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<number[]>([]);
  const [selectedLeadPrograms, setSelectedLeadPrograms] = useState<string[]>([]);
  
  const LEAD_PROGRAMS_LIST = [
    'Окружающий мир (1-3 кл.)',
    'Природоведение (4-5 кл.)',
    'География (6-8 кл.)',
    'Физика и астрономия (9-11 кл.)'
  ];
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  
  const [languages, setLanguages] = useState<any[]>([]);
  const AVAILABLE_LANGUAGES = ['Русский', 'Английский', 'Немецкий', 'Французский', 'Китайский'];
  const LANG_FLAGS: Record<string, string> = { 'Русский': '🇷🇺', 'Английский': '🇬🇧', 'Немецкий': '🇩🇪', 'Французский': '🇫🇷', 'Китайский': '🇨🇳' };
  const LANG_SHORT: Record<string, string> = { 'Русский': 'RU', 'Английский': 'EN', 'Немецкий': 'DE', 'Французский': 'FR', 'Китайский': 'CN' };
  const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Native'];

  useEffect(() => {
    fetchGuides();
    fetchTours();
  }, []);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/guides');
      setGuides(extractArrayFromResponse<Guide>(res));
    } catch (err) {
      showSnackbar('Ошибка загрузки гидов', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTours = async () => {
    try {
      const res = await axios.get('/api/tours');
      setTours(extractArrayFromResponse(res));
    } catch (err) {
      console.error('Ошибка загрузки программ:', err);
    }
  };

  const handleOpenModal = async (guide: Guide | null) => {
    setEditingGuide(guide);
    setTabValue(0);
    if (guide) {
      setFormData({
        ...guide,
        contract_date: formatDateForInput(guide.contract_date),
        contract_end_date: formatDateForInput(guide.contract_end_date || ''),
        is_active: Boolean(guide.is_active)
      });
      setSelectedLeadPrograms(guide.lead_programs ? (typeof guide.lead_programs === 'string' ? JSON.parse(guide.lead_programs) : guide.lead_programs) : []);
      await fetchGuideRelations(guide.id);
    } else {
      setFormData({ name: '', short_name: '', contract_number: '', contract_date: '', contract_end_date: '', max_id: '', is_active: true });
      setSelectedPrograms([]);
      setLanguages([]);
      setSelectedLeadPrograms([]);
    }
    setModalOpen(true);
  };

  const fetchGuideRelations = async (guideId: number) => {
    setLoadingPrograms(true);
    try {
      const [progRes, langRes] = await Promise.all([
        axios.get(`/api/guide-programs/${guideId}`),
        axios.get(`/api/guide-languages/${guideId}`)
      ]);
      setSelectedPrograms(progRes.data);
      setLanguages(langRes.data.map((l: any) => ({ ...l, checked: true })));
    } catch (err) {
      console.error('Ошибка загрузки связей:', err);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const handleSave = async () => {
    try {
      let guideId = editingGuide?.id;
      console.log('Saving lead_programs:', selectedLeadPrograms);
      const payload = { ...formData, lead_programs: JSON.stringify(selectedLeadPrograms), is_active: formData.is_active ? 1 : 0 };
      console.log('Payload:', payload);

      if (editingGuide) {
        await axios.put(`/api/guides/${guideId}`, payload);
      } else {
        const res = await axios.post('/api/guides', payload);
        guideId = res.data.id;
      }

      if (guideId) {
        await axios.post(`/api/guide-programs/${guideId}`, { programIds: selectedPrograms });
        await axios.post(`/api/guide-languages/${guideId}`, { languages: languages.filter(l => l.checked) });
      }

      showSnackbar('Сохранено успешно', 'success');
      setModalOpen(false);
      fetchGuides();
    } catch (err) {
      showSnackbar('Ошибка сохранения', 'error');
    }
  };

  
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async (guideId: number) => {
    if (!photoFile) return;
    const formData = new FormData();
    formData.append('photo', photoFile);
    try {
      const res = await axios.post(`http://192.168.50.43:5002/api/guides/${guideId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, photo_url: res.data.photo_url }));
      showSnackbar('Фото загружено', 'success');
    } catch (err) {
      showSnackbar('Ошибка загрузки фото', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Логика сортировки
  const handleRequestSort = (property: keyof Guide) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedGuides = [...guides].sort((a, b) => {
    const valA = a[orderBy] || '';
    const valB = b[orderBy] || '';
    if (order === 'asc') return valA < valB ? -1 : 1;
    return valA > valB ? -1 : 1;
  });

  const handleSelectAllPrograms = () => {
    const allTourIds = tours.map(t => t.id);
    const isAllSelected = allTourIds.every(id => selectedPrograms.includes(id));
    
    if (isAllSelected) {
      setSelectedPrograms([]); // Снимаем все
    } else {
      setSelectedPrograms(allTourIds); // Выделяем все
    }
  };

  const isAllProgramsSelected = tours.length > 0 && tours.every(t => selectedPrograms.includes(t.id));

  return (
    <Box sx={{ p: 3 }}>
      {/* Красивый заголовок как в других разделах */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>

        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal(null)} sx={{ borderRadius: 2 }}>
          Добавить экскурсовода
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ width: 60 }}>Фото</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Имя
                </TableSortLabel>
              </TableCell>
              <TableCell>Контракт</TableCell>
              <TableCell>Программы</TableCell>
              <TableCell>Языки</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : sortedGuides.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">Нет данных</TableCell></TableRow>
            ) : (
              sortedGuides.map((g) => {
                // Безопасный парсинг данных
                const progs = g.programs ? (typeof g.programs === 'string' ? JSON.parse(g.programs) : g.programs) : [];
                const langs = g.languages ? (typeof g.languages === 'string' ? JSON.parse(g.languages) : g.languages) : [];

                return (
                  <TableRow key={g.id} hover>
                    <TableCell>
                      <Avatar 
                        src={g.photo_url ? `http://192.168.50.43:5002${g.photo_url}` : undefined}
                        sx={{ width: 40, height: 40 }}
                      >
                        {!g.photo_url && g.name ? g.name[0] : ''}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{g.name}</TableCell>
                    <TableCell>{g.contract_number}</TableCell>
                    
                    {/* ПРОГРАММЫ */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {progs.length > 0 ? (
                          <>
                            {progs.slice(0, 2).map((prog: string, idx: number) => (
                              <Chip key={idx} label={prog} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                            ))}
                            {progs.length > 2 && (
                              <Tooltip title={progs.slice(2).join(', ')}>
                                <Chip label={`+${progs.length - 2}`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* ЯЗЫКИ */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {langs.length > 0 ? (
                          langs.map((lang: any, idx: number) => (
                            <Tooltip key={idx} title={`${lang.language}: ${lang.level}`}>
                              <Chip 
                                label={`${LANG_FLAGS[lang.language] || ''} ${LANG_SHORT[lang.language] || lang.language} (${lang.level})`} 
                                size="small" 
                                variant="outlined" 
                                sx={{ fontSize: '0.75rem', bgcolor: lang.language === 'Английский' ? '#e3f2fd' : 'transparent' }}
                              />
                            </Tooltip>
                          ))
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip label={g.is_active ? 'Активен' : 'Неактивен'} color={g.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenModal(g)} color="primary"><Edit /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingGuide ? 'Редактирование экскурсовода' : 'Новый экскурсовод'}
        maxWidth="md"
        actions={
          <>
            <Button onClick={() => setModalOpen(false)}>Отмена</Button>
            <Button onClick={handleSave} variant="contained">Сохранить</Button>
          </>
        }
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Основные" />
            <Tab label="Доступ" icon={<Lock fontSize="small" />} iconPosition="start" />
            <Tab label="Дополнительно" icon={<Smartphone fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* ВКЛАДКА 1: ОСНОВНЫЕ */}
        {tabValue === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Полное имя" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} fullWidth />
            <TextField label="Краткое имя" value={formData.short_name || ''} onChange={e => setFormData({...formData, short_name: e.target.value})} fullWidth />
            {/* Блок фото */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar 
                src={photoPreview || (formData.photo_url ? `http://192.168.50.43:5002${formData.photo_url}` : undefined)} 
                sx={{ width: 80, height: 80 }}
              />
              <Button variant="outlined" component="label">
                Загрузить фото
                <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
              </Button>
              {photoFile && (
                <Button variant="contained" color="primary" onClick={() => editingGuide && uploadPhoto(editingGuide.id)}>
                  Сохранить фото
                </Button>
              )}
            </Box>

            <TextField label="Номер контракта" value={formData.contract_number || ''} onChange={e => setFormData({...formData, contract_number: e.target.value})} fullWidth />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Дата контракта" type="date" value={formData.contract_date || ''} onChange={e => setFormData({...formData, contract_date: e.target.value})} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Дата окончания" type="date" value={formData.contract_end_date || ''} onChange={e => setFormData({...formData, contract_end_date: e.target.value})} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
            </Box>

            <FormControlLabel
              control={<Switch checked={!!formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />}
              label="Активен (может получать экскурсии)"
            />

            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Программы</Typography>
              <Tooltip title={isAllProgramsSelected ? "Снять все" : "Выделить все"}>
                <IconButton size="small" onClick={handleSelectAllPrograms} color={isAllProgramsSelected ? "primary" : "default"}>
                  <SelectAll />
                </IconButton>
              </Tooltip>
            </Box>
            {loadingPrograms ? <CircularProgress size={20} /> : (
              <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                {tours.map(tour => (
                  <FormControlLabel
                    key={tour.id}
                    control={<Checkbox checked={selectedPrograms.includes(tour.id)} onChange={e => {
                      if (e.target.checked) setSelectedPrograms([...selectedPrograms, tour.id]);
                      else setSelectedPrograms(selectedPrograms.filter(id => id !== tour.id));
                    }} />}
                    label={tour.name}
                    sx={{ display: 'block' }}
                  />
                ))}
              </Box>
            )}

            <Divider sx={{ my: 1 }} />
            <Typography variant="h6">Направления (подвиды экскурсий)</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Отметьте направления, которые ведёт экскурсовод. Это будет учитываться при автоматическом распределении.
            </Typography>
            <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
              {LEAD_PROGRAMS_LIST.map(prog => (
                <FormControlLabel
                  key={prog}
                  control={<Checkbox checked={selectedLeadPrograms.includes(prog)} onChange={e => {
                    if (e.target.checked) setSelectedLeadPrograms([...selectedLeadPrograms, prog]);
                    else setSelectedLeadPrograms(selectedLeadPrograms.filter(p => p !== prog));
                  }} />}
                  label={prog}
                  sx={{ display: 'block' }}
                />
              ))}
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>Языки</Typography>
            <Box sx={{ border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
              {AVAILABLE_LANGUAGES.map(lang => {
                const existing = languages.find(l => l.language === lang);
                return (
                  <Box key={lang} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FormControlLabel
                      control={<Checkbox checked={!!existing} onChange={e => {
                        if (e.target.checked) setLanguages([...languages, { language: lang, level: 'B2', checked: true }]);
                        else setLanguages(languages.filter(l => l.language !== lang));
                      }} />}
                      label={lang}
                      sx={{ minWidth: 150 }}
                    />
                    {existing && (
                      <TextField select size="small" value={existing.level || 'B2'} onChange={e => {
                        setLanguages(languages.map(l => l.language === lang ? { ...l, level: e.target.value } : l));
                      }} sx={{ width: 100 }}>
                        {LEVELS.map(lvl => <MenuItem key={lvl} value={lvl}>{lvl}</MenuItem>)}
                      </TextField>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ВКЛАДКА 2: ДОСТУП */}
        {tabValue === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', py: 4 }}>
            <Lock sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6">Управление доступом по HTTPS</Typography>
            <Typography variant="body2" color="textSecondary" align="center" sx={{ maxWidth: 400 }}>
              Здесь будет настройка логина и пароля для входа экскурсовода в личный кабинет. (Функционал в разработке)
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 300, display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField label="Логин (email)" disabled variant="outlined" size="small" />
              <TextField label="Пароль" type="password" disabled variant="outlined" size="small" />
              <Button variant="outlined" disabled>Выдать доступ</Button>
            </Box>
          </Box>
        )}

        {/* ВКЛАДКА 3: ДОПОЛНИТЕЛЬНО */}
        {tabValue === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Smartphone color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h6">Интеграция с мессенджером MAX</Typography>
                <Typography variant="body2" color="textSecondary">Укажите ID пользователя для отправки уведомлений и доступа к мини-приложению.</Typography>
              </Box>
            </Box>
            
            <TextField 
              label="ID пользователя в MAX" 
              value={formData.max_id || ''} 
              onChange={e => setFormData({...formData, max_id: e.target.value})} 
              fullWidth 
              placeholder="Например: 123456789"
              helperText="Этот ID будет использоваться ботом для связи с экскурсоводом."
            />
          </Box>
        )}

      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GuidesPage;
